<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WhatsappFlowsAPI from 'dashboard/api/whatsappFlows';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import FlowIssuesDialog from 'dashboard/routes/dashboard/whatsappFlows/FlowIssuesDialog.vue';
import { focusFlowField } from 'dashboard/routes/dashboard/whatsappFlows/flowIssues';

// Envio direto de um WhatsApp Flow pela conversa. Usa o mesmo caminho das mensagens
// interativas (content_type whatsapp_interactive, tipo flow) do Funil e do modal legado.
// Somente Flows da caixa da conversa; rascunhos exigem o modo de teste explícito.
// O backend revalida caixa, conta, status do Flow e a janela de 24 horas.
const props = defineProps({
  inboxId: { type: [Number, String], default: null },
  accountId: { type: [Number, String], default: null },
  canReply: { type: Boolean, default: true },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['send', 'close']);
const { t } = useI18n();

const LIMITS = { cta: 20, body: 1024, header: 60, footer: 60 };

const dialogRef = ref(null);
const issuesRef = ref(null);
const flows = ref([]);
const canManage = ref(false);
const isLoadingFlows = ref(false);
const showErrors = ref(false);
const focusOnIssuesClose = ref(false);
const form = reactive({
  flowId: '',
  testMode: false,
  cta: '',
  body: '',
  header: '',
  footer: '',
});

const sendableFlows = computed(() =>
  flows.value.filter(
    flow =>
      String(flow.inbox_id) === String(props.inboxId) &&
      flow.meta_flow_id &&
      (flow.status === 'PUBLISHED' ||
        (form.testMode && flow.status === 'DRAFT'))
  )
);

const hasDrafts = computed(() =>
  flows.value.some(
    flow =>
      String(flow.inbox_id) === String(props.inboxId) && flow.status === 'DRAFT'
  )
);

const flowOptions = computed(() =>
  sendableFlows.value.map(flow => ({
    value: String(flow.id),
    label:
      flow.status === 'DRAFT'
        ? `${flow.name} · ${t('WHATSAPP_FLOWS.STATUS.DRAFT')}`
        : flow.name,
  }))
);

const selectedFlow = computed(() =>
  sendableFlows.value.find(flow => String(flow.id) === form.flowId)
);
const firstScreen = computed(() => selectedFlow.value?.first_screen_id || '');

// Mensagem padrão salva no Flow (WhatsApp Flows > Mensagem de envio) já preenche o envio.
// Só troca o que o atendente não editou: campo vazio ou igual ao padrão do Flow anterior.
const DEFAULT_FIELDS = {
  cta: 'button_text',
  header: 'header',
  body: 'body',
  footer: 'footer',
};
let appliedDefaults = {};
watch(selectedFlow, flow => {
  const defaults = flow?.send_defaults || {};
  Object.entries(DEFAULT_FIELDS).forEach(([field, key]) => {
    const untouched = !form[field] || form[field] === appliedDefaults[field];
    if (untouched) form[field] = defaults[key] || '';
  });
  appliedDefaults = Object.fromEntries(
    Object.entries(DEFAULT_FIELDS).map(([field, key]) => [
      field,
      defaults[key] || '',
    ])
  );
});
const isDraft = computed(() => selectedFlow.value?.status === 'DRAFT');

const manageUrl = computed(() => {
  if (!props.accountId) return '';
  return frontendURL(
    `accounts/${props.accountId}/campanhas/whatsapp-flows`,
    props.inboxId ? { inbox_id: props.inboxId } : {}
  );
});

const tooLong = (value, limit) => value.trim().length > limit;

const formIssues = computed(() => {
  const issues = [];
  if (!props.canReply) {
    issues.push({ key: null, message: t('WHATSAPP_FLOWS.SEND.WINDOW') });
  }
  if (!selectedFlow.value) {
    issues.push({
      key: 'flow',
      message: t('WHATSAPP_FLOWS.SEND.ERRORS.FLOW'),
      hint: t('WHATSAPP_FLOWS.HINTS.REQUIRED'),
    });
  } else if (!firstScreen.value) {
    issues.push({
      key: 'flow',
      message: t('WHATSAPP_FLOWS.SEND.ERRORS.SCREEN'),
      hint: t('WHATSAPP_FLOWS.SEND.ERRORS.SCREEN'),
    });
  }
  if (!form.cta.trim() || tooLong(form.cta, LIMITS.cta)) {
    issues.push({
      key: 'cta',
      message: t('WHATSAPP_FLOWS.SEND.ERRORS.CTA'),
      hint: t('WHATSAPP_FLOWS.SEND.HINTS.CTA'),
    });
  }
  if (!form.body.trim() || tooLong(form.body, LIMITS.body)) {
    issues.push({
      key: 'body',
      message: t('WHATSAPP_FLOWS.SEND.ERRORS.BODY'),
      hint: t('WHATSAPP_FLOWS.SEND.HINTS.BODY'),
    });
  }
  if (tooLong(form.header, LIMITS.header)) {
    issues.push({
      key: 'header',
      message: t('WHATSAPP_FLOWS.SEND.ERRORS.HEADER'),
      hint: t('WHATSAPP_FLOWS.SEND.HINTS.LIMIT_60'),
    });
  }
  if (tooLong(form.footer, LIMITS.footer)) {
    issues.push({
      key: 'footer',
      message: t('WHATSAPP_FLOWS.SEND.ERRORS.FOOTER'),
      hint: t('WHATSAPP_FLOWS.SEND.HINTS.LIMIT_60'),
    });
  }
  return issues;
});

const hintFor = key => {
  if (!showErrors.value) return '';
  return formIssues.value.find(issue => issue.key === key)?.hint || '';
};

const loadFlows = async () => {
  isLoadingFlows.value = true;
  try {
    const { data } = await WhatsappFlowsAPI.list({ inbox_id: props.inboxId });
    flows.value = data.payload || [];
    canManage.value = !!data.can_manage;
  } catch (error) {
    flows.value = [];
    issuesRef.value?.open({
      title: t('WHATSAPP_FLOWS.ISSUES.LOAD_ERROR_TITLE'),
      issues: [error?.response?.data?.error || t('WHATSAPP_FLOWS.LOAD_ERROR')],
    });
  } finally {
    isLoadingFlows.value = false;
  }
};

const reset = () => {
  Object.assign(form, {
    flowId: '',
    testMode: false,
    cta: '',
    body: '',
    header: '',
    footer: '',
  });
  showErrors.value = false;
  focusOnIssuesClose.value = false;
  appliedDefaults = {};
};

const open = () => {
  reset();
  flows.value = [];
  dialogRef.value?.open();
  loadFlows();
};

const close = () => dialogRef.value?.close();

const toggleTestMode = value => {
  form.testMode = value;
  if (!value && isDraft.value) form.flowId = '';
};

const showIssues = (options, focusFirstField = false) => {
  focusOnIssuesClose.value = focusFirstField;
  issuesRef.value?.open(options);
};

const handleIssuesClose = async () => {
  if (!focusOnIssuesClose.value) return;
  focusOnIssuesClose.value = false;
  await nextTick();
  focusFlowField(formIssues.value.find(issue => issue.key)?.key);
};

// Recusas do servidor já chegam em linguagem humana e são exibidas no modal do sistema.
const showError = message =>
  showIssues({
    title: t('WHATSAPP_FLOWS.SEND.ERROR_TITLE'),
    issues: [message || t('WHATSAPP_FLOWS.SEND.ERROR')],
  });

const generateFlowToken = () =>
  `talkvia_${crypto.randomUUID().replaceAll('-', '')}`;

const buildPayload = () => {
  const flow = selectedFlow.value;
  const header = form.header.trim();
  const interactive = {
    type: 'flow',
    flow_id: flow.meta_flow_id,
    flow_token: generateFlowToken(),
    flow_cta: form.cta.trim(),
    flow_action: 'navigate',
    flow_screen: firstScreen.value,
    header_type: header ? 'text' : undefined,
    header_text: header || undefined,
    footer_text: form.footer.trim() || undefined,
    flow_mode: flow.status === 'DRAFT' ? 'draft' : undefined,
  };
  return {
    message: form.body.trim(),
    private: false,
    contentType: 'whatsapp_interactive',
    contentAttributes: { interactive, whatsapp_flow_record_id: flow.id },
  };
};

const handleConfirm = () => {
  if (props.isLoading) return;
  showErrors.value = true;
  if (formIssues.value.length) {
    showIssues(
      {
        title: t('WHATSAPP_FLOWS.SEND.REVIEW_TITLE'),
        description: t('WHATSAPP_FLOWS.SEND.REVIEW_DESCRIPTION'),
        issues: formIssues.value.map(issue => issue.message),
        confirmLabel: t('WHATSAPP_FLOWS.ISSUES.REVIEW'),
      },
      true
    );
    return;
  }
  emit('send', buildPayload());
};

defineExpose({ open, close, showError });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    overflow-y-auto
    sticky-header-footer
    solid
    :close-on-click-outside="false"
    :title="t('WHATSAPP_FLOWS.SEND.TITLE')"
    :description="t('WHATSAPP_FLOWS.SEND.DESCRIPTION')"
    :confirm-button-label="t('WHATSAPP_FLOWS.SEND.SUBMIT')"
    :disable-confirm-button="!canReply"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="emit('close')"
  >
    <div class="grid items-start gap-5 md:grid-cols-[minmax(0,1fr)_16rem]">
      <div class="flex min-w-0 flex-col gap-4">
        <p
          v-if="!canReply"
          class="mb-0 flex items-start gap-2 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-3 text-sm text-n-ruby-12"
          role="status"
        >
          <span class="i-hugeicons-clock-01 mt-0.5 size-4 shrink-0" />
          {{ t('WHATSAPP_FLOWS.SEND.WINDOW') }}
        </p>

        <div class="flex flex-col gap-1.5" data-flow-field="flow">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('WHATSAPP_FLOWS.SEND.FLOW') }}
            </span>
            <a
              v-if="canManage && manageUrl"
              :href="manageUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
            >
              <span class="i-hugeicons-link-square-02 size-3.5" />
              {{ t('WHATSAPP_FLOWS.SEND.MANAGE') }}
            </a>
          </div>
          <ComboBox
            v-model="form.flowId"
            :options="flowOptions"
            :display-label="selectedFlow?.name || ''"
            :placeholder="
              isLoadingFlows
                ? t('WHATSAPP_FLOWS.SEND.LOADING')
                : t('WHATSAPP_FLOWS.SEND.FLOW_PLACEHOLDER')
            "
            :search-placeholder="t('WHATSAPP_FLOWS.SEND.SEARCH')"
            :empty-state="t('WHATSAPP_FLOWS.SEND.EMPTY')"
            :disabled="isLoadingFlows"
            :has-error="!!hintFor('flow')"
            :message="hintFor('flow')"
            class="w-full"
          />
          <p
            v-if="!isLoadingFlows && !flowOptions.length"
            class="mb-0 text-xs text-n-slate-11"
          >
            {{ t('WHATSAPP_FLOWS.SEND.EMPTY_HELP') }}
          </p>
          <p
            v-else-if="selectedFlow && firstScreen"
            class="mb-0 text-xs text-n-slate-11"
          >
            {{ t('WHATSAPP_FLOWS.SEND.FIRST_SCREEN', { screen: firstScreen }) }}
          </p>
        </div>

        <div v-if="hasDrafts || form.testMode" class="flex flex-col gap-2">
          <label
            class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-12"
          >
            <Checkbox
              :model-value="form.testMode"
              @update:model-value="toggleTestMode"
            />
            {{ t('WHATSAPP_FLOWS.SEND.TEST_MODE') }}
          </label>
          <p
            v-if="form.testMode"
            class="mb-0 flex items-start gap-2 rounded-xl border border-n-amber-6 bg-n-amber-2 p-3 text-xs text-n-amber-12"
          >
            <span class="i-hugeicons-alert-circle mt-0.5 size-4 shrink-0" />
            {{ t('WHATSAPP_FLOWS.SEND.TEST_MODE_WARNING') }}
          </p>
        </div>

        <div class="grid gap-3 sm:grid-cols-2">
          <NextInput
            v-model="form.cta"
            data-flow-field="cta"
            :label="t('WHATSAPP_FLOWS.SEND.CTA')"
            :placeholder="t('WHATSAPP_FLOWS.SEND.CTA_PLACEHOLDER')"
            :maxlength="String(LIMITS.cta)"
            :message="hintFor('cta') || t('WHATSAPP_FLOWS.SEND.HINTS.CTA')"
            :message-type="hintFor('cta') ? 'error' : 'info'"
          />
          <NextInput
            v-model="form.header"
            data-flow-field="header"
            :label="t('WHATSAPP_FLOWS.SEND.HEADER')"
            :maxlength="String(LIMITS.header)"
            :message="hintFor('header')"
            :message-type="hintFor('header') ? 'error' : 'info'"
          />
        </div>
        <NextTextArea
          v-model="form.body"
          data-flow-field="body"
          :label="t('WHATSAPP_FLOWS.SEND.BODY')"
          :placeholder="t('WHATSAPP_FLOWS.SEND.BODY_PLACEHOLDER')"
          :max-length="LIMITS.body"
          :message="hintFor('body')"
          :message-type="hintFor('body') ? 'error' : 'info'"
          show-character-count
          resize
        />
        <NextInput
          v-model="form.footer"
          data-flow-field="footer"
          :label="t('WHATSAPP_FLOWS.SEND.FOOTER')"
          :maxlength="String(LIMITS.footer)"
          :message="hintFor('footer')"
          :message-type="hintFor('footer') ? 'error' : 'info'"
        />
      </div>

      <aside class="flex flex-col gap-2 md:sticky md:top-0">
        <p
          class="mb-0 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('WHATSAPP_FLOWS.SEND.PREVIEW') }}
        </p>
        <div class="rounded-2xl bg-n-alpha-2 p-3">
          <div
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-sm"
          >
            <div class="flex flex-col gap-1 px-3 py-2.5">
              <p
                v-if="form.header.trim()"
                class="mb-0 break-words text-sm font-semibold text-n-slate-12"
              >
                {{ form.header.trim() }}
              </p>
              <p
                class="mb-0 whitespace-pre-wrap break-words text-sm text-n-slate-12"
              >
                {{ form.body.trim() || t('WHATSAPP_FLOWS.SEND.BODY_PREVIEW') }}
              </p>
              <p
                v-if="form.footer.trim()"
                class="mb-0 break-words text-xs text-n-slate-11"
              >
                {{ form.footer.trim() }}
              </p>
            </div>
            <div
              class="flex items-center justify-center gap-1.5 border-t border-n-weak px-3 py-2 text-sm font-medium text-n-blue-11"
            >
              <span class="i-hugeicons-workflow-square-06 size-4" />
              <span class="truncate">
                {{ form.cta.trim() || t('WHATSAPP_FLOWS.SEND.CTA_PREVIEW') }}
              </span>
            </div>
          </div>
        </div>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('WHATSAPP_FLOWS.SEND.PREVIEW_HELP') }}
        </p>
      </aside>
    </div>
  </Dialog>
  <FlowIssuesDialog ref="issuesRef" @close="handleIssuesClose" />
</template>
